/**
 * 外观设置单点（任务 14.31a）：主题模式 / 主题色 / 字号 / 聊天背景。
 *
 * 全部纯本地（localStorage，设备级非账号级——旧版 GetStorage 同口径），
 * 无服务端接口。持久化形状 = AppearanceSettings 的 JSON。
 *
 * ⚠️ index.html 内联脚本是本文件的镜像（首屏前同步应用防闪色，无法 import 模块）：
 * 存储 key、档位 id 集、字号范围、暗色底色改这里必须同步改那边——
 * appearanceModel.test.ts 会解析 index.html 交叉校验，漏改单测红。
 *
 * 旧版对照（theme_service.dart）：
 * - 6 主题色/5 聊天背景色值逐一照搬；id 重命名（telegram_blue→sky_blue 等）——
 *   新版存储独立无迁移负担，且不复用 Telegram 名称。
 * - 模式新增 'system'（旧版仅 light/dark，legacy §6.5 建议项）。
 * - 字号 14-22 默认 16，textScale = clamp(size/16, 0.9, 1.4) 同公式；
 *   作用范围=消息气泡文本（刻意收窄：会话列表/FixedList 是定高行，全局缩放会
 *   顶破行高；旧版 Flutter 全局 textScaleFactor 无此约束）。
 * - 暗色下聊天背景渐变被忽略、直接用页面底色（旧版 _chatBackgroundColors 同口径）。
 */

export type ThemeMode = 'light' | 'dark' | 'system';

export interface AccentOption {
  id: string;
  label: string;
  /** 主色 hex（global.scss [data-accent] 块与此同源，改色两处一起改） */
  color: string;
}

export interface ChatBgOption {
  id: string;
  label: string;
  /** 渐变起止色（global.scss [data-chat-bg] 块同源） */
  from: string;
  to: string;
}

export const APPEARANCE_STORAGE_KEY = 'lhc.appearance.v1';

export const ACCENT_OPTIONS: readonly AccentOption[] = [
  { id: 'sky_blue', label: '天空蓝', color: '#2aabee' },
  { id: 'deep_blue', label: '经典深蓝', color: '#0088cc' },
  { id: 'china_red', label: '中国红', color: '#e53935' },
  { id: 'bamboo_green', label: '青竹绿', color: '#54cb68' },
  { id: 'sunset_purple', label: '晚霞紫', color: '#d669ed' },
  { id: 'twilight_orange', label: '暮光橙', color: '#ff885e' },
];

export const CHAT_BG_OPTIONS: readonly ChatBgOption[] = [
  { id: 'default', label: '默认气泡', from: '#d4e2ec', to: '#e7eff5' },
  { id: 'sky', label: '晴空蓝', from: '#eaf6ff', to: '#d6ecff' },
  { id: 'mint', label: '薄荷绿', from: '#e8fff5', to: '#d7f7ea' },
  { id: 'sunset', label: '落日橙', from: '#fff1e6', to: '#ffdec8' },
  { id: 'night', label: '夜幕灰蓝', from: '#2a3441', to: '#384657' },
];

export const MIN_FONT_SIZE = 14;
export const MAX_FONT_SIZE = 22;
export const DEFAULT_FONT_SIZE = 16;

/** 暗色页面底色（global.scss --color-bg-page 暗色值；meta theme-color 用） */
export const DARK_THEME_COLOR = '#0f1720';
/** 浅色 meta theme-color（index.html 静态值；pwa-meta.spec 断言同值） */
export const LIGHT_THEME_COLOR = '#ffffff';

export interface AppearanceSettings {
  mode: ThemeMode;
  accent: string;
  fontSize: number;
  chatBg: string;
}

export const DEFAULT_APPEARANCE: AppearanceSettings = {
  mode: 'light',
  accent: 'sky_blue',
  fontSize: DEFAULT_FONT_SIZE,
  chatBg: 'default',
};

/** 容忍坏 JSON/缺字段/越界值：逐字段回落默认（旧版 init 同语义） */
export function normalizeAppearance(raw: unknown): AppearanceSettings {
  const src = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const mode =
    src.mode === 'dark' || src.mode === 'system' || src.mode === 'light'
      ? src.mode
      : DEFAULT_APPEARANCE.mode;
  const accent = ACCENT_OPTIONS.some((o) => o.id === src.accent)
    ? (src.accent as string)
    : DEFAULT_APPEARANCE.accent;
  const chatBg = CHAT_BG_OPTIONS.some((o) => o.id === src.chatBg)
    ? (src.chatBg as string)
    : DEFAULT_APPEARANCE.chatBg;
  const fs = typeof src.fontSize === 'number' ? src.fontSize : NaN;
  const fontSize = fs >= MIN_FONT_SIZE && fs <= MAX_FONT_SIZE ? fs : DEFAULT_FONT_SIZE;
  return { mode, accent, fontSize, chatBg };
}

/** 旧版 textScaleFactor 同公式：clamp(size/16, 0.9, 1.4) */
export function fontScaleOf(fontSize: number): number {
  return Math.min(1.4, Math.max(0.9, fontSize / DEFAULT_FONT_SIZE));
}

export function resolveDark(mode: ThemeMode, systemDark: boolean): boolean {
  return mode === 'dark' || (mode === 'system' && systemDark);
}

/** 设置 → DOM 落点计划（null = 移除该属性/变量，回到 :root 默认）。
 *  纯函数可单测；index.html 内联脚本与 14.31c 的运行时应用器共用此语义。 */
export interface AppearanceDomPlan {
  theme: 'dark' | null;
  accent: string | null;
  chatBg: string | null;
  fontScale: string | null;
  themeColor: string;
}

export function appearanceDomPlan(s: AppearanceSettings, systemDark: boolean): AppearanceDomPlan {
  const dark = resolveDark(s.mode, systemDark);
  return {
    theme: dark ? 'dark' : null,
    accent: s.accent === DEFAULT_APPEARANCE.accent ? null : s.accent,
    chatBg: s.chatBg === DEFAULT_APPEARANCE.chatBg ? null : s.chatBg,
    fontScale: s.fontSize === DEFAULT_FONT_SIZE ? null : String(fontScaleOf(s.fontSize)),
    themeColor: dark ? DARK_THEME_COLOR : LIGHT_THEME_COLOR,
  };
}

/** 读 localStorage（坏值容忍）；storage 可注入供单测 */
export function readAppearance(storage: Pick<Storage, 'getItem'>): AppearanceSettings {
  let raw: unknown = null;
  try {
    raw = JSON.parse(storage.getItem(APPEARANCE_STORAGE_KEY) ?? 'null');
  } catch {
    raw = null;
  }
  return normalizeAppearance(raw);
}
